import { useState, useEffect, useReducer, useMemo } from "react";
import { CustomTooltip } from "../nexus/nexus.js";
import FindInPageTwoToneIcon from "@mui/icons-material/FindInPageTwoTone";
import { IosShare } from "@mui/icons-material";
import VisibilityOff from "../scenes/VisibilityOff.js";
import VisibilityOn from "../scenes/VisibilityOn.js";
import {
  sortAlpha,
  setUnique,
  finderSelectStyles,
  exportToExcel,
} from "../nexus/finder.js";
import useColumnsDR from "../hooks/useColumnsDR.js";
import { HiSortAscending, HiSortDescending } from "react-icons/hi";
import DatePicker from "react-datepicker";
import { Button } from "@mui/material";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  useSortBy,
  useBlockLayout,
  useResizeColumns,
  useRowSelect,
} from "react-table";
import finderStyles from "../styles/finder.module.css";
import dfStyles from "../styles/delFinder.module.css";
import { BiReset } from "react-icons/bi";
import { RiArrowDownSLine, RiArrowRightSLine } from "react-icons/ri";
import axios from "axios";
import moment from "moment";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { ThreeDots } from "react-loader-spinner";
import { useSelector } from "react-redux";

export default function DelReportFinder() {
  const [reports, setReports] = useState([]);
  const [empty, setEmpty] = useState(false);
  const [columns, setColumns] = useState(useColumnsDR());
  const userToken = useSelector((state) => state.userToken);

  const columnMap = {
    id: { label: "ID", width: 10 },
    viewTitle: { label: "Title", width: 25 },
    reportType: { label: "Type", width: 10 },
    deletedBy: { label: "Deleted By", width: 12 },
    deletedDate: { label: "Deleted Date", width: 10 },
    justification: { label: "Justification", width: 15 },
    justificationOther: { label: "Other", width: 20 },
    createdBy: { label: "Author", width: 12 },
    from: { label: "Reporting Period", width: 10 },
  };
  const renewColumns = () => {
    setColumns((prevColumns) =>
      prevColumns.map((column) => ({
        ...column,
        show: !column.showInCopy ? true : column.copy ? false : column.show,
      }))
    );
  };

  useEffect(() => {
    renewColumns();
    setGlobalFilter("");
  }, []);
  const filterInitialState = {
    isOpen: {
      id: false,
      type: false,
      author: false,
      dateFrom: false,
      period: false,
      from: false,
      deletedBy: false,
      justification: false,
    },
    reportIds: [],
    authors: [],
    types: ["Fortnightly", "Monthly"],
    id: [],
    createdBy: [],
    dateFrom: null,
    dateTo: null,
    periodFrom: null,
    periodTo: null,
    modFrom: null,
    modTo: null,
    removers: [],
    deletedBy: [],
    justifications: [],
  };

  const reducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
      case "SET_IS_OPEN":
        return {
          ...state,
          isOpen: {
            ...state.isOpen,
            [payload.type]: payload.value,
          },
        };
      case "RESET_FILTERS":
        const {
          authors,
          removers,
          reportIds,
          justifications,
          ...filteredInitialState
        } = filterInitialState;
        return {
          ...state,
          ...filteredInitialState,
        };

      default:
        return { ...state, [type]: payload };
    }
  };
  const [filterState, dispatch] = useReducer(reducer, filterInitialState);
  const {
    isOpen,
    reportIds,
    authors,
    types,
    id,
    createdBy,
    dateFrom,
    dateTo,
    periodFrom,
    periodTo,
    modFrom,
    modTo,
    removers,
    deletedBy,
    justification,
    justifications,
  } = filterState;

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (isLoggedIn === null || isLoggedIn !== "true") {
      window.location.href = "/";
    }
    fetchData();
    // const storedItems = JSON.parse(sessionStorage.getItem("finderItems"));
    // if (!storedItems) {
    //   fetchData(); // Perform action when finderItems exist in localStorage
    // } else {
    //   setReports(storedItems);
    //   setFilterData(storedItems);
    // }
  }, []);

  const fetchData = async () => {
    try {
      // const response = await axios.get("/getAllReports");
      const response = await axios.get("/getDelReports", {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });
      if (response.data.length === 0) {
        setEmpty(true);
      }
      const modifiedItems = response.data.map((item) => {
        const { workflow, ...rest } = item;

        //const fromDate = moment(item.from, "DD/MM/YYYY");

        const viewTitle = item.title;
        const deletedDate = moment(
          item.deletedDate.split(" ")[0],
          "D/M/YYYY"
        ).format("DD/MM/YYYY");

        // Create the modified item object
        const modifiedItem = {
          ...rest,
          deletedDate,
          status: workflow ? workflow.currentStatus : "Deleted",
          viewTitle,
        };

        return modifiedItem;
      });

      setReports(modifiedItems);
      setFilterData(modifiedItems);
    
    } catch (error) {
      console.error("Error while fetching items:", error);
    }
  };

  const setFilterData = (items) => {
    const justifications = setUnique(items, "justification");

    dispatch({
      type: "justifications",
      payload: justifications.map((jus) => jus.label),
    });

    const reportIds = sortAlpha(setUnique(items, "id"));
    dispatch({ type: "reportIds", payload: reportIds });

    const authors = setUnique(items, "createdBy");
    dispatch({ type: "authors", payload: sortAlpha(authors) });

    const removers = setUnique(items, "deletedBy");
    dispatch({ type: "removers", payload: sortAlpha(removers) });
  };

  const data = useMemo(() => reports, [reports]);
  const defaultColumn = useMemo(
    () => ({
      minWidth: 30,
      width: 150,
      maxWidth: 400,
    }),
    []
  );
  const tableInstance = useTable(
    {
      columns,
      initialState: {
        hiddenColumns: columns.map((column) => {
          if (column.show === false) return column.accessor || column.id;
        }),
        sortBy: [
          {
            id: "from",
            desc: true,
          },
        ],
      },
      data,
      defaultColumn,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useBlockLayout,
    useResizeColumns,
    useRowSelect
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setFilter,
    state,
    setGlobalFilter,
    setAllFilters,
    toggleRowSelected,
  } = tableInstance;

  const { globalFilter } = state;

  const handleSearch = (e) => {
    setGlobalFilter(e.target.value || undefined);
  };

  const handleHide = (accessor) => {
    setColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.accessor === accessor
          ? { ...column, show: !column.show }
          : column
      )
    );
  };

  const handleRangeValues = (value, prevFilters, isChecked) => {
    const [start, end] = value.split("-");
    const lowerBound = parseInt(start);
    const upperBound = parseInt(end);
    const rangeValues = [];

    for (let i = lowerBound; i <= upperBound; i++) {
      rangeValues.push(i.toString());
    }

    return isChecked
      ? [...prevFilters, ...rangeValues]
      : prevFilters.filter((value) => !rangeValues.includes(value));
  };

  const handleSelect = (columnId, e) => {
    const filters = (prevFilters = []) => {
      const value = e.target.value;
      const isChecked = e.target.checked;

      if (value.includes("-")) {
        return handleRangeValues(value, prevFilters, isChecked);
      }

      return isChecked
        ? [...prevFilters, value]
        : prevFilters.filter((filter) => filter !== value);
    };

    setFilter(columnId, filters);
    dispatch({ type: columnId, payload: filters });
  };

  const handleDateToChange = (columnId, colPart, from, date) => {
    dispatch({ type: colPart, payload: date });
    setFilter(columnId, [from, date]);
  };

  const handleDateChange = (columnId, colPart, colToPart, date) => {
    dispatch({ type: colPart, payload: date });
    if (colToPart != null) {
      dispatch({ type: colToPart, payload: null });
      setFilter(columnId, "");
    }
    setFilter(columnId, [date]);
  };

  const handleMultiChange = (selOptions, field) => {
    dispatch({ type: field, payload: selOptions });
    const values = selOptions.map((item) => item.value);
    setFilter(field, values);
  };
  const handleClearFilters = () => {
    setAllFilters([]);
    dispatch({ type: "RESET_FILTERS" });
    setGlobalFilter("");
  };

  const filterData = [
    {
      accessor: "id",
      label: "ID",
      type: "id",
      data: reportIds,
      control: "select",
      name: "filtersId",
      placeholder: "Select Report Id/s...",
      styles: finderSelectStyles,
      value: id,
    },
    {
      accessor: "reportType",
      label: "Type",
      type: "type",
      data: types,
      control: "check",
    },
    {
      accessor: "createdBy",
      label: "Author",
      type: "author",
      data: authors,
      control: "select",
      name: "filtersAuthor",
      placeholder: "Select Author/s...",
      styles: finderSelectStyles,
      value: createdBy,
    },
    {
      accessor: "deletedBy",
      label: "Deleted By",
      type: "deletedBy",
      data: removers,
      control: "select",
      name: "filtersRemover",
      placeholder: "Select Deleted By/s...",
      styles: finderSelectStyles,
      value: deletedBy,
    },
    {
      accessor: "from",
      label: "Reporting Period",
      type: "from",
      control: "date",
      start: dateFrom,
      startString: "dateFrom",
      end: dateTo,
      endString: "dateTo",
    },

    {
      accessor: "deletedDate",
      label: "Date Deleted",
      type: "isOpenFrom",
      control: "date",
      start: modFrom,
      startString: "modFrom",
      end: modTo,
      endString: "modTo",
    },
    {
      accessor: "justification",
      label: "Justification",
      type: "Justification",
      data: justifications,
      control: "check",
    },
  ];

  return (
    <div className={finderStyles.container}>
      {/* <div className={rfStyles.leftNavBar}>
        <NavBar />
      </div> */}

      <div className={finderStyles.gridContainer}>
        <div className={finderStyles.leftSection}>
          <div
            className={finderStyles.clearFiltersContainer}
            onClick={handleClearFilters}
          >
            <BiReset className={finderStyles.clearFiltersIcon} />
            <span className={finderStyles.clearFiltersText}>Reset Filters</span>
          </div>
          <div className={finderStyles.filterSection}>
            {filterData.map((filter) => {
              return (
                <div className={finderStyles.filter} key={filter.accessor}>
                  {columns.find((column) => column.accessor === filter.accessor)
                    ?.show ? (
                    <>
                      {isOpen[filter.type] ? (
                        <RiArrowDownSLine
                          className={finderStyles.iconFilter}
                          onClick={() =>
                            dispatch({
                              type: "SET_IS_OPEN",
                              payload: { type: filter.type, value: false },
                            })
                          }
                        />
                      ) : (
                        <RiArrowRightSLine
                          className={finderStyles.iconFilter}
                          onClick={() =>
                            dispatch({
                              type: "SET_IS_OPEN",
                              payload: { type: filter.type, value: true },
                            })
                          }
                        />
                      )}
                      <label className={finderStyles.lblFilter}>
                        {filter.label}
                        {filter.type === "ragG" && (
                          <label className={finderStyles.greenBox}></label>
                        )}
                        {filter.type === "ragG" && (
                          <label
                            className={finderStyles.colorBox}
                            style={{ backgroundColor: "green" }}
                          ></label>
                        )}
                        {filter.type === "ragO" && (
                          <label
                            className={finderStyles.colorBox}
                            style={{ backgroundColor: "orange" }}
                          ></label>
                        )}
                        {filter.type === "ragR" && (
                          <label
                            className={finderStyles.colorBox}
                            style={{ backgroundColor: "#FF4500" }}
                          ></label>
                        )}
                      </label>
                    </>
                  ) : (
                    <>
                      <RiArrowRightSLine
                        className={finderStyles.iconFilterHide}
                      />
                      <label className={finderStyles.lblFilterDisabled}>
                        {filter.label}
                        {filter.type === "ragG" && (
                          <label
                            className={finderStyles.colorBox}
                            style={{ backgroundColor: "green" }}
                          ></label>
                        )}
                        {filter.type === "ragO" && (
                          <label
                            className={finderStyles.colorBox}
                            style={{ backgroundColor: "orange" }}
                          ></label>
                        )}
                        {filter.type === "ragR" && (
                          <label
                            className={finderStyles.colorBox}
                            style={{ backgroundColor: "#FF4500" }}
                          ></label>
                        )}
                      </label>
                    </>
                  )}

                  {isOpen[filter.type] ? (
                    <div className={finderStyles.filterContent}>
                      {filter.control === "check" &&
                        filter.data.map((val) => (
                          <Form.Check
                            type="checkbox"
                            label={val}
                            value={val}
                            id={val}
                            onClick={(e) => handleSelect(filter.accessor, e)}
                          />
                        ))}
                      {filter.control === "date" && (
                        <div className={finderStyles.datepickerWrapper}>
                          <DatePicker
                            selected={filter.start}
                            onChange={(date) =>
                              handleDateChange(
                                filter.accessor,
                                filter.startString,
                                filter.endString,
                                date
                              )
                            }
                            className={finderStyles.datePicker}
                            dateFormat="dd/MM/y"
                            placeholderText="Pick date..."
                          />

                          <label>To</label>
                          <DatePicker
                            selected={filter.end}
                            onChange={(date) =>
                              handleDateToChange(
                                filter.accessor,
                                filter.endString,
                                filter.start,
                                date
                              )
                            }
                            minDate={filter.start}
                            dateFormat="dd/MM/y"
                            placeholderText="Pick date..."
                            disabled={filter.start === null}
                            className={finderStyles.datePicker}
                          />
                        </div>
                      )}
                      {filter.control === "select" && (
                        <Select
                          name={filter.name}
                          placeholder={filter.placeHolder}
                          styles={filter.styles}
                          value={filter.value}
                          options={filter.data}
                          onChange={(selOptions) =>
                            handleMultiChange(selOptions, filter.accessor)
                          }
                          isMulti
                        />
                      )}
                    </div>
                  ) : columns.find(
                      (column) => column.accessor === filter.accessor
                    )?.show ? (
                    <VisibilityOn
                      onClick={() => handleHide(filter.accessor)}
                      className={finderStyles.iconColsShow}
                    />
                  ) : (
                    <VisibilityOff
                      onClick={() => handleHide(filter.accessor)}
                      className={finderStyles.iconColsHide}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className={finderStyles.rightSection}>
          <div className={dfStyles.topSection}>
            <FindInPageTwoToneIcon className={dfStyles.finderIcon} />
            <label className={dfStyles.topHeader}>Deleted Report Finder</label>
            <div className={finderStyles.searchBox}>
              <Form.Control
                type="text"
                placeholder=""
                value={globalFilter || ""}
                onChange={handleSearch}
                className={finderStyles.searchControl}
              />
            </div>
            {!empty && (
              <Button
                startIcon={<IosShare className={finderStyles.buttonIcon} />}
                variant="contained"
                onClick={() =>
                  exportToExcel(
                    columnMap,
                    tableInstance,
                    rows,
                    "Deleted_Report"
                  )
                }
                className={finderStyles.button}
              >
                Export to Excel
              </Button>
            )}
          </div>

          <div className={finderStyles.countSection}>
            {rows.length === 1
              ? `${rows.length} Result`
              : `${rows.length} Results`}
          </div>
          {empty ? (
            <div className={dfStyles.naContainer}>
              <div className={dfStyles.naMessage}>
                <h2>No Deleted Reports Found</h2>
              </div>
            </div>
          ) : (
            <div className={finderStyles.bottomSection}>
              <table {...getTableProps()} className={finderStyles.grid}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                        >
                          <CustomTooltip title="Click to sort">
                            {column.render("Header")}
                          </CustomTooltip>

                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <HiSortDescending
                                className={finderStyles.sortIcon}
                              />
                            ) : (
                              <HiSortAscending
                                className={finderStyles.sortIcon}
                              />
                            )
                          ) : (
                            ""
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                {reports.length > 0 ? (
                  <tbody
                    className={finderStyles.reportContent}
                    {...getTableBodyProps()}
                  >
                    {rows.map((row) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => (
                            <td {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                ) : (
                  <div className={finderStyles.loaderOverlay}>
                    <div className={finderStyles.loaderContainer}>
                      <ThreeDots color="#186d90" height={150} width={150} />
                    </div>
                  </div>
                )}
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
